import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { formatDistanceToNow, format } from "date-fns";
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationIcon,
  SearchIcon,
} from "@heroicons/react/outline";
import {
  getPendingPaymentsThunk,
  verifyPaymentThunk,
  rejectPaymentThunk,
} from "../../../../store/thunks/jewelleryPlan/paymentThunks";
import VerificationModal from "./VerificationModal";

const PendingVerifications = () => {
  const dispatch = useDispatch();

  // Use correct Redux state path and property names
  const {
    pendingPayments = [],
    pendingSummary = {
      total: 0,
      totalAmount: 0,
      averageAge: 0,
      aging: {},
    },
    loading = false,
  } = useSelector((state) => state.jewelleryPlan.payments || {});

  const { primaryBranch } = useSelector((state) => state.branch || {});

  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [ageFilter, setAgeFilter] = useState("all");

  useEffect(() => {
    // Only fetch if primaryBranch exists
    if (primaryBranch?._id) {
      dispatch(getPendingPaymentsThunk({ branchId: primaryBranch._id }));
    }
  }, [dispatch, primaryBranch]);

  const handleVerify = (payment) => {
    setSelectedPayment(payment);
    setShowVerificationModal(true);
  };

  const handleQuickVerify = async (payment) => {
    if (payment.hasVariance) {
      handleVerify(payment);
      return;
    }

    try {
      await dispatch(
        verifyPaymentThunk({
          paymentId: payment._id,
          verificationData: {
            verifiedAmount: payment.amountCollected,
            note: "Quick verified - no variance",
          },
        })
      ).unwrap();
    } catch (error) {
      console.error("Verification failed:", error);
    }
  };

  const handleReject = async (payment, reason) => {
    try {
      await dispatch(
        rejectPaymentThunk({
          paymentId: payment._id,
          rejectionData: { reason },
        })
      ).unwrap();
    } catch (error) {
      console.error("Rejection failed:", error);
    }
  };

  const filteredPayments = pendingPayments.filter((payment) => {
    // Search filter
    const matchesSearch =
      !searchTerm ||
      payment.planId?.planNumber
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      payment.userId?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.customerId?.name
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase());

    // Age filter - use the ageBucket property from the payment
    let matchesAge = true;
    if (ageFilter !== "all") {
      matchesAge = payment.ageBucket === ageFilter;
    }

    return matchesSearch && matchesAge;
  });

  const getAgeColor = (ageInHours) => {
    if (typeof ageInHours === "number") {
      if (ageInHours <= 24) return "text-success";
      if (ageInHours <= 72) return "text-warning";
      if (ageInHours <= 168) return "text-warning-dark";
      return "text-danger";
    }
    // Fallback to calculating from createdAt if ageInHours not provided
    return "text-neutral-dark";
  };

  // Calculate totals for filtered payments when age filter is active
  const getFilteredTotal = (ageBucket) => {
    return pendingPayments
      .filter((p) => p.ageBucket === ageBucket)
      .reduce((sum, p) => sum + (p.amountCollected || 0), 0);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="bg-background shadow rounded-lg p-6 mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-text">
              Payment Verifications
            </h1>
            <p className="mt-1 text-sm text-neutral-dark">
              Review and verify pending payments
            </p>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold text-primary">
              {pendingSummary.total || 0}
            </p>
            <p className="text-sm text-neutral-dark">Pending</p>
            {pendingSummary.totalAmount > 0 && (
              <p className="text-xs text-neutral-dark mt-1">
                ₹{pendingSummary.totalAmount.toLocaleString()}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Summary Cards - Based on aging counts */}
      {Object.keys(pendingSummary.aging || {}).length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          {Object.entries(pendingSummary.aging).map(([key, count]) => {
            const amount = getFilteredTotal(key);
            return (
              <div
                key={key}
                className={`bg-background rounded-lg p-4 border cursor-pointer transition-all
                  ${
                    ageFilter === key
                      ? "border-primary shadow-lg"
                      : "border-neutral-light hover:shadow-md"
                  }`}
                onClick={() => setAgeFilter(ageFilter === key ? "all" : key)}
              >
                <p className="text-sm text-neutral-dark">
                  {key === "0-24h" && "0 - 24 hours"}
                  {key === "1-3d" && "1 - 3 days"}
                  {key === "3-7d" && "3 - 7 days"}
                  {key === "7d+" && "7+ days"}
                </p>
                <p className="text-2xl font-bold text-text">{count || 0}</p>
                <p className="text-xs text-neutral-dark mt-1">
                  ₹{amount.toLocaleString()}
                </p>
              </div>
            );
          })}
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-background shadow rounded-lg p-4 mb-6">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SearchIcon className="h-5 w-5 text-neutral-dark" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by plan number or customer name..."
                className="block w-full pl-10 pr-3 py-2 border border-neutral-light rounded-md focus:ring-primary focus:border-primary"
              />
            </div>
          </div>
          {ageFilter !== "all" && (
            <button
              onClick={() => setAgeFilter("all")}
              className="px-3 py-2 text-sm font-medium text-text bg-neutral-light rounded-md hover:bg-neutral-dark hover:text-background"
            >
              Clear Filter
            </button>
          )}
        </div>
      </div>

      {/* Payments Table */}
      {filteredPayments.length === 0 ? (
        <div className="bg-background shadow rounded-lg p-12 text-center">
          <CheckCircleIcon className="h-12 w-12 text-success mx-auto mb-4" />
          <p className="text-neutral-dark">
            {searchTerm || ageFilter !== "all"
              ? "No payments match your filters"
              : "No pending verifications"}
          </p>
        </div>
      ) : (
        <div className="bg-background shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-neutral-light">
            <thead className="bg-neutral-light">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                  Plan / Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                  Method
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                  Collected
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                  Age
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                  Entered By
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-neutral-dark uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-background divide-y divide-neutral-light">
              {filteredPayments.map((payment) => (
                <tr key={payment._id} className="hover:bg-neutral-light">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <p className="text-sm font-medium text-text">
                        {payment.planId?.planNumber || "N/A"}
                      </p>
                      <p className="text-xs text-neutral-dark">
                        {payment.userId?.name ||
                          payment.customerId?.name ||
                          "Unknown"}
                        {payment.userId?.phoneNumber &&
                          ` / ${payment.userId.phoneNumber}`}
                      </p>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <p className="text-sm font-medium text-text">
                        ₹{(payment.amountCollected || 0).toLocaleString()}
                      </p>
                      {payment.hasVariance && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-warning-light text-warning-dark">
                          <ExclamationIcon className="h-3 w-3 mr-1" />
                          Variance {payment.variance && `₹${payment.variance}`}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text">
                    {payment.method || "N/A"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text">
                    {payment.dateCollected
                      ? format(new Date(payment.dateCollected), "dd MMM yyyy")
                      : "N/A"}
                  </td>
                  <td
                    className={`px-6 py-4 whitespace-nowrap text-sm ${
                      payment.ageInHours !== undefined
                        ? getAgeColor(payment.ageInHours)
                        : "text-neutral-dark"
                    }`}
                  >
                    {/* Show age bucket label if available, otherwise calculate from createdAt */}
                    {payment.ageBucket ? (
                      <span>
                        {payment.ageBucket === "0-24h" && "< 1 day"}
                        {payment.ageBucket === "1-3d" && "1-3 days"}
                        {payment.ageBucket === "3-7d" && "3-7 days"}
                        {payment.ageBucket === "7d+" && "7+ days"}
                      </span>
                    ) : payment.createdAt ? (
                      formatDistanceToNow(new Date(payment.createdAt), {
                        addSuffix: true,
                      })
                    ) : (
                      "N/A"
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-dark">
                    {payment.enteredBy?.name || payment.enteredBy || "System"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleQuickVerify(payment)}
                      className="text-success hover:text-success-dark mr-3"
                      title="Quick Verify"
                    >
                      <CheckCircleIcon className="h-5 w-5 inline" />
                    </button>
                    <button
                      onClick={() => handleVerify(payment)}
                      className="text-secondary hover:text-secondary-dark mr-3"
                      title="Review & Verify"
                    >
                      Review
                    </button>
                    <button
                      onClick={() => {
                        const reason = prompt("Rejection reason:");
                        if (reason) handleReject(payment, reason);
                      }}
                      className="text-danger hover:text-danger-dark"
                      title="Reject"
                    >
                      <XCircleIcon className="h-5 w-5 inline" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Verification Modal */}
      {showVerificationModal && selectedPayment && (
        <VerificationModal
          payment={selectedPayment}
          onClose={() => {
            setShowVerificationModal(false);
            setSelectedPayment(null);
          }}
          onVerify={async (data) => {
            await dispatch(
              verifyPaymentThunk({
                paymentId: selectedPayment._id,
                verificationData: data,
              })
            ).unwrap();
            setShowVerificationModal(false);
            setSelectedPayment(null);
          }}
        />
      )}
    </div>
  );
};

export default PendingVerifications;
